import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  FileText,
  History,
  Lock,
  LockOpen,
  RotateCcw,
  Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/common/hooks/usePermissions';
import { invoicesService } from '@/features/invoices/services/invoices.service';
import { validateMonthSearch } from '@/features/invoices/invoice-search';
import { MonthSelect } from '@/features/invoices/components/MonthSelect';
import { CloseBillingDialog } from '@/features/invoices/components/CloseBillingDialog';
import { ReopenBillingDialog } from '@/features/invoices/components/ReopenBillingDialog';
import { MonthInvoicesTable } from '@/features/invoices/components/MonthInvoicesTable';
import { ReminderHistoryDialog } from '@/features/invoices/components/ReminderHistoryDialog';
import { InvoicesTimeseriesChart } from '@/features/invoices/components/InvoicesTimeseriesChart';
import {
  formatCurrency,
  formatDate,
  formatMonthCapitalized,
} from '@/features/invoices/components/invoice-formatters';

export const Route = createFileRoute('/_authenticated/admin/invoices/')({
  validateSearch: validateMonthSearch,
  component: AdminInvoicesDashboardPage,
});

function AdminInvoicesDashboardPage() {
  const { referenceMonth } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/invoices/' });
  const { hasPermission } = usePermissions();
  const canManageInvoices = hasPermission('invoices.manage');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [closeBillingOpen, setCloseBillingOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const {
    data: stats,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['invoice-stats', referenceMonth || 'open'],
    queryFn: () => invoicesService.getStats(referenceMonth),
    placeholderData: (prev) => prev,
  });

  const activeMonth = referenceMonth ?? stats?.referenceMonth ?? '';
  /** Ignora placeholderData de outra competência enquanto o mês da URL troca. */
  const statsForActiveMonth =
    stats && stats.referenceMonth === activeMonth ? stats : undefined;

  const { data: billingPeriods } = useQuery({
    queryKey: ['billing-periods'],
    queryFn: () => invoicesService.getBillingPeriods(),
    enabled: !!statsForActiveMonth?.isClosed && canManageInvoices,
  });
  const closedPeriod = statsForActiveMonth?.isClosed
    ? billingPeriods?.find((period) => period.referenceMonth === activeMonth)
    : undefined;
  const monthSearch = activeMonth ? { referenceMonth: activeMonth } : {};

  const handleMonthChange = (month: string) => {
    navigate({ search: { referenceMonth: month } });
  };

  if (isLoading && !stats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[120px]" />
          ))}
        </div>
        <Skeleton className="h-[300px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Notas Fiscais</h1>
          <p className="text-muted-foreground">Gerencie as notas fiscais dos prestadores PJ</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MonthSelect value={activeMonth} onChange={handleMonthChange} />
          <Button
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            disabled={!activeMonth}
          >
            <History className="mr-2 h-4 w-4" />
            Histórico de alertas
          </Button>
          {canManageInvoices &&
            (statsForActiveMonth?.isClosed ? (
              <Button
                variant="outline"
                onClick={() => setReopenOpen(true)}
                disabled={!activeMonth || !closedPeriod}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reabrir Período
              </Button>
            ) : (
              <Button
                onClick={() => setCloseBillingOpen(true)}
                disabled={!activeMonth || !statsForActiveMonth}
              >
                <Calendar className="mr-2 h-4 w-4" />
                Fechar Período
              </Button>
            ))}
        </div>
      </div>

      {isError && !stats ? (
        <Card>
          <CardContent className="py-8 text-center space-y-3">
            <p className="text-muted-foreground">Não foi possível carregar as estatísticas.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Mês de Referência</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-1">
                <div className="text-2xl font-bold">
                  {activeMonth ? formatMonthCapitalized(activeMonth) : '-'}
                </div>
                <div className="flex items-center gap-2">
                  {!statsForActiveMonth ? (
                    <Skeleton className="h-5 w-24" />
                  ) : statsForActiveMonth.isClosed ? (
                    <Badge variant="secondary" className="gap-1">
                      <Lock className="h-3 w-3" />
                      Fechado
                      {statsForActiveMonth.closedAt
                        ? ` em ${formatDate(statsForActiveMonth.closedAt)}`
                        : ''}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1">
                      <LockOpen className="h-3 w-3" />
                      Aberto
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Último faturado:{' '}
                  {statsForActiveMonth?.lastBilledMonth
                    ? formatMonthCapitalized(statsForActiveMonth.lastBilledMonth)
                    : 'Nenhum'}
                  {statsForActiveMonth?.isClosed && statsForActiveMonth.closedBy?.name
                    ? ` · por ${statsForActiveMonth.closedBy.name}`
                    : ''}
                </p>
              </CardContent>
            </Card>

            <Link
              to="/admin/invoices/submitted"
              search={{ ...monthSearch }}
              className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full transition-colors hover:bg-muted/50">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">PJ — Total de Prestadores</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statsForActiveMonth?.totalPj ?? '—'}</div>
                  <p className="text-xs text-muted-foreground">Prestadores PJ ativos</p>
                </CardContent>
              </Card>
            </Link>

            <Link
              to="/admin/invoices/submitted"
              search={{ ...monthSearch, filter: 'submitted' }}
              className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full transition-colors hover:bg-muted/50">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Notas Enviadas</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {statsForActiveMonth?.submittedCount ?? '—'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {activeMonth ? `Em ${formatMonthCapitalized(activeMonth)}` : 'Do período'}
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link
              to="/admin/invoices/missing"
              search={{ ...monthSearch }}
              className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full transition-colors hover:bg-muted/50">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Notas Faltantes</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {statsForActiveMonth?.missingCount ?? '—'}
                  </div>
                  <p className="text-xs text-muted-foreground">Prestadores sem nota</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {statsForActiveMonth?.isClosed ? 'Período fechado' : 'Antes de fechar o período'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {!statsForActiveMonth ? (
                <Skeleton className="h-16 w-full" />
              ) : statsForActiveMonth.isClosed ? (
                <p>
                  As notas desta competência foram marcadas como pagas. Reabra o período para
                  permitir novos envios; os pagamentos registrados são mantidos.
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <div className="text-xl font-semibold text-foreground">
                      {statsForActiveMonth.pendingCount}
                    </div>
                    <p>Nota(s) pendente(s) de pagamento</p>
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-foreground">
                      {formatCurrency(statsForActiveMonth.pendingValue)}
                    </div>
                    <p>Valor pendente a ser pago ao fechar</p>
                  </div>
                  <div>
                    <div
                      className={`text-xl font-semibold ${
                        statsForActiveMonth.missingCount > 0 ? 'text-red-600' : 'text-foreground'
                      }`}
                    >
                      {statsForActiveMonth.missingCount}
                    </div>
                    <p>Prestador(es) sem nota enviada</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {activeMonth && <InvoicesTimeseriesChart referenceMonth={activeMonth} />}
          {activeMonth && <MonthInvoicesTable referenceMonth={activeMonth} />}
        </>
      )}

      <ReminderHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        referenceMonth={activeMonth}
      />
      {canManageInvoices && (
        <CloseBillingDialog
          open={closeBillingOpen}
          onOpenChange={setCloseBillingOpen}
          defaultMonth={activeMonth}
        />
      )}
      {canManageInvoices && (
        <ReopenBillingDialog
          open={reopenOpen}
          onOpenChange={setReopenOpen}
          referenceMonth={activeMonth}
          billingPeriodId={closedPeriod?.id}
        />
      )}
    </div>
  );
}
